import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ConfigService } from '../../../../services/config.service';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { passwordValidator } from '../../../../_core/validators/password.validator';
import { confirmPasswordValidator } from '../../../../_core/validators/confirmPassword.validator';
import { UserFacade } from '../../../../_core/state/user/user.facade';
import { ApiService } from '../../../../services/api.service';
import { MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { LoginDialogComponent } from '../../login-dialog/login-dialog.component';
import { LoginFacade } from '../../../state/login.facade';
import { runValidator } from '../../../../signup/validators/run.validator';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-first-login',
  templateUrl: './first-login.component.html',
  styleUrls: ['./first-login.component.scss'],
})
export class FirstLoginComponent implements OnInit {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly dialogRef = inject(MatLegacyDialogRef<LoginDialogComponent>);
  private readonly loginFacade = inject(LoginFacade);
  private readonly userFacade = inject(UserFacade);

  readonly firstLoginConfig = signal(this.configService.get().firstLoginConfig);
  readonly message = computed(() => this.firstLoginConfig().message);
  readonly showTermsConditions = computed(() => this.firstLoginConfig().showTermsConditions);
  readonly showPasswordChange = computed(() => this.firstLoginConfig().showPasswordChange);
  readonly showEmailChange = computed(() => this.firstLoginConfig().showEmailChange);
  readonly showRutChange = computed(() => this.firstLoginConfig().showRutChange);
  readonly termsUrl = signal<string>(this.configService.get().termsUrl);

  readonly formGroup = new FormGroup({});

  get rutControl() {
    return this.formGroup.get('rut');
  }

  get emailControl() {
    return this.formGroup.get('email');
  }

  get passwordControl() {
    return this.formGroup.get('password.newPassword');
  }

  get confirmPasswordControl() {
    return this.formGroup.get('password.confirmNewPassword');
  }

  get termsControl() {
    return this.formGroup.get('terms');
  }

  ngOnInit(): void {
    if (this.showPasswordChange()) {
      this.formGroup.addControl('password', this.buildPasswordFormControl());
    }

    if (this.showTermsConditions()) {
      this.formGroup.addControl('terms', this.buildTermsCheckboxFormControl());
    }

    if (this.showEmailChange()) {
      this.formGroup.addControl('email', this.buildEmailFormControl());
    }

    if (this.showRutChange()) {
      this.formGroup.addControl('rut', this.buildRutFormControl());
    }
  }

  validateData(event: Event): void {
    event.preventDefault();

    const user = {
      ...this.userFacade.user,
      termsAccepted: true,
    };

    if (this.showPasswordChange()) {
      user.newPassword = this.passwordControl.value;
    }

    if (this.showEmailChange()) {
      user.email = this.emailControl.value;
    }

    if (this.showRutChange()) {
      user.externalId = this.rutControl.value;
    }

    this.apiService.updateUser(user).subscribe((u) => {
      this.authService.updateUser(u);
      this.dialogRef.close(false);
      this.loginFacade.closeDialog();
    });
  }

  private buildRutFormControl(): AbstractControl {
    return new FormControl('', [Validators.required, runValidator]);
  }

  private buildEmailFormControl(): AbstractControl {
    return new FormControl('', [Validators.required, Validators.email]);
  }

  private buildPasswordFormControl(): AbstractControl {
    const newPasswordControl = new FormControl('', [Validators.required, passwordValidator()]);
    const confirmNewPasswordControl = new FormControl('', [
      Validators.required,
      confirmPasswordValidator(newPasswordControl),
    ]);

    newPasswordControl?.valueChanges.subscribe(() => {
      confirmNewPasswordControl?.updateValueAndValidity();
    });

    return new FormGroup({
      newPassword: newPasswordControl,
      confirmNewPassword: confirmNewPasswordControl,
    });
  }

  private buildTermsCheckboxFormControl(): AbstractControl {
    return new FormControl(false, [Validators.requiredTrue]);
  }
}
